import React from 'react';
import { cn } from '@/utils';
import { progressBarTrackStyles, progressBarFillStyles } from './ProgressBar.styles';

export interface ProgressBarProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Accessible label for the progress bar (used for screen readers).
   */
  ariaLabel?: string;

  /**
   * The current progress value (0-100).
   */
  value: number;

  /**
   * The maximum value. Default is 100.
   */
  max?: number;

  /**
   * The minimum value. Default is 0.
   */
  min?: number;

  /**
   * Progress bar size. Default is 'medium'.
   */
  size?: 'small' | 'medium' | 'large';

  /**
   * Progress bar variant which defines the visual style. Default is 'primary'.
   */
  variant?: 'primary' | 'secondary' | 'success' | 'warning' | 'error';

  /**
   * Whether to show the percentage text. Default is false.
   */
  showPercentage?: boolean;

  /**
   * Custom background color for the progress track.
   */
  trackColor?: string;

  /**
   * Custom color for the progress fill.
   */
  fillColor?: string;
}

export const ProgressBar: React.FC<ProgressBarProps> = ({
  ariaLabel,
  className,
  value,
  max = 100,
  min = 0,
  size = 'medium',
  variant = 'primary',
  showPercentage = false,
  trackColor,
  fillColor,
  ...props
}) => {
  // Ensure value is within bounds
  const clampedValue = Math.min(Math.max(value, min), max);
  
  // Calculate percentage
  const percentage = ((clampedValue - min) / (max - min)) * 100;
  
  // Get the variant classes
  const trackClasses = cn(
    progressBarTrackStyles({ size, variant })
  );
  
  const fillClasses = cn(
    progressBarFillStyles({ variant })
  );
  
  // Combine all class names
  const containerClasses = cn(
    'relative w-full',
    className
  );

  return (
    <div
      className={containerClasses}
      {...props}
    >
      <div
        className={trackClasses}
        style={trackColor ? { backgroundColor: trackColor } : undefined}
        role="progressbar"
        aria-valuenow={clampedValue}
        aria-valuemin={min}
        aria-valuemax={max}
        aria-label={ariaLabel || `Progress: ${Math.round(percentage)}%`}
        data-testid="progress-bar"
      >
        <div
          className={fillClasses}
          style={{
            width: `${percentage}%`,
            ...(fillColor ? { backgroundColor: fillColor } : {}),
          }}
        />
      </div>
      {showPercentage && (
        <div className="mt-2 text-sm text-neutral-gray-600 text-center">
          {Math.round(percentage)}%
        </div>
      )}
    </div>
  );
};

ProgressBar.displayName = 'ProgressBar';

export default ProgressBar;